import { Component, OnInit, Inject, ChangeDetectionStrategy, OnDestroy, ViewChild, ChangeDetectorRef } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatInput } from "@angular/material";
import { Appointment } from "./appointment";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { BarbersService } from "../barbers/barbers.service";
import { ServicesService } from "../services/services.service";
import { Inventory } from "../inventory/inventory";
import { SettingsService } from "../settings/settings.service";
import { SettingsRow } from "../settings/settings.defs";
import { BarberBlock, AppointmentRow } from "./appointments.defs";
import { Barber } from "../barbers/barber";
import { AppointmentConfirmDialogComponent, AppointmentConfirmDialogParams } from "./appointment-confirm-dialog.component";
import { ClientsService } from "../clients/clients.service";
import { Client } from "../clients/client";
import { BehaviorSubject } from "rxjs";
import { AppointmentsService } from "./appointments.service";
import { AppService } from "../app/app.service";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { CallerIdService, CallLog } from "../phone/caller-id.service";
import { ClientComponent } from "../clients/client.component";

export interface AppointmentComponentData {
	appointment: Appointment;
	schedule: BarberBlock[];
}

// 51373, Barber

@Component({
    selector: 'app-appointment',
    templateUrl: './appointment.component.html',
    styleUrls: ['./appointment.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentComponent implements OnInit, OnDestroy {

    services: Inventory[];
    duration = '0:00';
    end: Date;
    settings: SettingsRow;
    selectedServices = new Set<number>();
    id: number;
    time: Date;
    nextAppointment: Date;
    homeTime: Date;
    lastNumberStorageKey = 'lastNumberSaved';

    barber: Barber;

    clients = new BehaviorSubject<Client[]>([]);
    existingClient: Client;

    form = new FormGroup({
        phone: new FormControl('', [Validators.maxLength(10), Validators.minLength(10)]),
        name: new FormControl(''),
        allowContact: new FormControl(false),
        anyBarber: new FormControl(false),
        reoccurring: new FormControl(0),
    });

    private _duration: number;
    private _appointment: Appointment;
    private _keyPressDisabled = false;

    calls: CallLog[] = [];
    // calls: CallLog[] = [new CallLog({date: '2018-12-01', time: '11:23', nmbr: '810-288-5197', name: 'foot', ncidtype: 'CID'})];
    callHistorySize = 10;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: AppointmentComponentData,
        private _barbers: BarbersService,
        private _services: ServicesService,
        private _settings: SettingsService,
        private _clients: ClientsService,
        private _dialog: MatDialog,
        private _appointments: AppointmentsService,
        private _self: MatDialogRef<AppointmentComponent>,
        public app: AppService,
        private _callerId: CallerIdService,
        private _cd: ChangeDetectorRef,
    ) {
    }

    get lastNumberUsed(): string {
        return localStorage.getItem(this.lastNumberStorageKey) || '';
    }

    get recoccuringHint(): Date {
        const days = this.form.get('reoccurring').value;
        if (days) {
            const d = new Date(this.time);
            d.setSeconds(0,0);
            d.setDate(d.getDate() + days);
            return d;
        }  else {
            return null;
        }
    }

    manageClient() {
        // we need to disconnect the window listener temporarily

        this._keyPressDisabled = true;

        const ref = this._dialog.open(ClientComponent, {
            data: this.form.get('phone').value,
            disableClose: true,
        })
        ref.afterClosed()
            .subscribe((changed: boolean) => {
                this._keyPressDisabled = true;
                if (changed) {
                    this._cd.detectChanges();
                }
            })
    }

    lastNumberUsedClick() {
        const ln = this.lastNumberUsed,
            p = this.form.get('phone');
        if (ln) {
            p.setValue(ln);
            this.phoneInputChanged();
            setTimeout(() => {
                this.nameInput.focus();
            });
        }
    }

    lastCallerClick() {
        const first = this.calls[0],
            p = this.form.get('phone');

        if (first) {
            p.setValue(first.number);
            this.phoneInputChanged();
        }
    }

    noShow() {

        const today = this.app.isToday,
            activity = today ? 'NO SHOW' : 'CANCEL',
            markedAs = today ? 'a no show' : 'cancelled',
            ref = this._dialog.open(ConfirmDialogComponent, {
                data: {message: `Are you sure you want to ${activity} this appointment?`}
            })

        ref.afterClosed()
            .subscribe(yep => {
                if (yep) {
                    this._appointments.update({id: this.id, cancelled_0: true})
                        .subscribe((resp) => {
                            const a = this._appointment;
                            a.cancelled = true;
                            a.incomplete = false;
                            this.app.notify(`The appointment was marked as ${markedAs}`);
                            this._self.close(a);
                        })
                }
            });
    }

    toggleClient(client: Client) {
        this.existingClient = this.existingClient === client ? null : client;
    }

    saveLunch() {
        // its possible that this is getting converted to a lunch
        // from an existing record, so we may need to convert ->

    }

    phoneInputChanged() {

        const p = this.form.get('phone'),
            val = p.value,
            sel = this.existingClient,
            name = ('' + this.form.value.name).toLowerCase().trim();
        if (p.valid && val && val.length === 10) {

            const seed = this._clients.getByPhone(val);

            if (
                !seed.length ||
                (sel && !seed.find(c => c.id === sel.id))
            ) {
                this.existingClient = null;
            } else if (seed.length && !sel && name) {
                const match = seed.find(c => c.name.toLowerCase() === name);
                this.existingClient = match || null;
            } else if (seed.length) { // in this case, there are multiple phone numbers found - why?
                // check to see if seed has a closer match via the name
                let match: Client;
                if (sel) {
                    match = seed.find(row => row.name === sel.name)
                }
                if (!match) {
                    match = seed[0];
                }

                this.existingClient = match;
                // check to see if there
            }
            this.clients.next(seed);
        } else {
            this.clients.next([]);
            this.existingClient = null;
        }
        this.checkAutocomplete();
    }

    inject(key: number | 'home' | 'clear' | 'backspace') {
        const p = this.form.get('phone');
        let val: string = '' + (p.value || '');
        switch (key) {
            case 'home':
                val = '5199670010';
                break;
            case 'clear':
                val = '';
                break;
            case 'backspace':
                val = val.substr(0, val.length - 1);
                break;
            default:
                if (val.length < 10) {
                    val += '' + key;
                }
        }
        p.setValue(val);
        this.phoneInputChanged();
    }

    toggleService(id: number) {
        const sel = this.selectedServices,
            lunch = this.settings.lunchServiceRef;
        sel[sel.has(id) ? 'delete' : 'add'](id);
        if (sel.has(lunch)) {
            sel.forEach((key, val) => {
                if (key !== lunch) {
                    sel.delete(key);
                }

            })
        }
        // is this lunch service?  then we need to make sure it's the only one selected
        this._updateDuration();
    }

    private _updateDuration() {

        const sel = this.selectedServices,
            barber = this.barber,
            dur = this._duration = this.services
                .filter(s => sel.has(s.id))
                .map(s => s.getDuration(barber ? barber.id : null))
                .reduce((a, b) => a+=b, 0),
            hours = Math.floor(dur / 60),
            mins = '' + (dur - (hours * 60)),
            end = this.end = new Date(this.time);
        this.duration = `${hours}:${mins.padEnd(2, '0')}`;
        end.setMinutes(end.getMinutes() + dur);
    }

    isToday() {
        const d = new Date(),
            sel = this.time;
        if (sel) {
            return d.getFullYear() === sel.getFullYear() && d.getMonth() === sel.getMonth() && d.getDate() === sel.getDate();
        }
        return false;
    }

    save() {
        const appointment = this._appointment,
            sel = this.selectedServices,
            services = this._services.all.getValue().filter(s => sel.has(s.id)),
            client = this.existingClient,
            form = this.form,
            formValue = form.value,
            number = formValue.phone,
            name = formValue.name,
            anyBarber = formValue.anyBarber,
            reoccurring: number = !anyBarber && appointment.barber ? formValue.reoccurring : 0,
            data: AppointmentConfirmDialogParams = {
                appointment,
                duration: this._duration,
                durationLabel: this.duration,
                services: services,
                isLunch: sel.has(this.settings.lunchServiceRef),
                anyBarber,
                reoccurring
            };

        let sendClient;

        if (client && name && client.name.toLowerCase() !== name.toLowerCase()) {
            sendClient = {name, number}; // force as new, let the backend attempt to marry them
        } else {
            sendClient = client || {name, number};
        }

        appointment.anyBarber = formValue.anyBarber;

        const ref = this._dialog.open(AppointmentConfirmDialogComponent, {
            data,
        });

        ref.afterClosed()
            .subscribe((confirm: boolean) => {
                if (confirm) {
                    localStorage.setItem(this.lastNumberStorageKey, number);
                    this._appointments
                        .save(appointment, sendClient, services, reoccurring)
                        .subscribe(resp => {
                            if (resp) {
                                if (reoccurring) {
                                    this.app.notify('Your record was created, along with 6 additional future dated appointments');
                                } else {
                                    this.app.notify(`Your record was saved`);
                                }
                                this._self.close(true);
                            } else {
                                this.app.notify(`There was a problem saving your appointment`, '', 10000);
                            }
                        });
                }
            })
    }

    get canComplete(): boolean {
        const now = new Date(),
            start = this.time;
        return this.id && !!(now > start);
    }

    complete() {
        if (this.canComplete) {
            this._appointments
                .update({
                    id: this.id,
                    noInvoiceComplete: true,
                })
                .subscribe(resp => {
                    this.app.notify('Your appointment was marked as complete');
                    this._self.close(true);
                })
        }
    }

    delete() {
        const id = this.id;
        if (id) {
            this._appointments
                .delete(id)
                .subscribe(resp => {
                    this.app.notify('Your record was deleted');
                    this._self.close(true);
                })
        }
    }

    ngOnInit() {

        // this.barbers = this._barbers.all.getValue();
        this.services = this._services.all.getValue();

        this._callerId.calls.subscribe(call => {
            if (!call) return;
            const c = this.calls;
            c.unshift(call);
            if (c.length > this.callHistorySize) {
                c.pop();
            }
        });

        const settings = this.settings = this._settings.active,
            d = this.data,
            a = d ? d.appointment : false,
            f = this.form;

        if (d) {

            // this.schedule = d.schedule;

            if (a) {
                this._appointment = a;
                this.id = a.id || 0;

                if (a.client) {
                    f.get('phone').setValue(a.client.tel);

                    this.existingClient = a.client;
                    if (a.client.tel === settings.noNumber_tel) {
                        f.get('name').setValue(a.client.name);
                    }
                }

                if (a.date) {
                    a.date.setSeconds(0,0);
                    this.time = a.date;
                }

                if (a.barber) {
                    this.barber = a.barber;
                    const sched = d.schedule.find(s => s.id === a.barber.id),
                        time = this.time.getTime();

                    if (sched) {
                        this.homeTime = sched.end;

                        // find any appointments that are after this time
                        const nextAppt = sched.appointments
                                .filter(appt => !appt.cancelled && appt.date.getTime() > time)
                                .sort()
                                [0];
                        if (nextAppt) {
                            this.nextAppointment = nextAppt.date;
                        }

                    }
                }

                f.get('anyBarber').setValue(!!(a.anyBarber));

                if (a.services) {
                    a.services
                        .filter(s => !!s)
                        .forEach(s => {
                            this.selectedServices.add(s.id);
                        })
                    this._updateDuration();
                }

                this.phoneInputChanged();
            }
        }
        // this._checkEverything();
        this._addKeyListeners();
    }

    ngOnDestroy() {
        this._removeKeyListeners();
    }

    numberFocus = false;
    nameFocus = false;
    @ViewChild('nameInput', {read: MatInput}) nameInput: MatInput;
    @ViewChild('phoneInput', {read: MatInput}) phoneInput: MatInput;

    private _onKeyPress: (evt: KeyboardEvent) => void;

    private _addKeyListeners() {
        window.addEventListener('keypress', this._onKeyPress = (evt) => {
            if (this._keyPressDisabled) return;
            const code = evt.key,
                numeric = /^[0-9]$/.test(code),
                alpha = /^[a-z]$/i.test(code);

            if ((alpha && this.numberFocus) || (numeric && this.nameFocus)) {
                evt.preventDefault();
            }

            if (numeric && !this.numberFocus) {
                this.inject(+code);
                if (this.phoneInput) {
                    evt.preventDefault();
                    setTimeout(() => {
                        this.phoneInput.focus();
                        this.checkAutocomplete();
                    });
                }
                // focus on the input at this point
            } else if (alpha && !this.nameFocus) {
                const ctl = this.form.get('name');
                ctl.setValue(ctl.value + code);
                if (this.nameInput) {
                    evt.preventDefault();
                    setTimeout(() => {
                        this.nameInput.focus();
                        this.checkAutocomplete();
                    });
                }
            }
        });
    }
    private _removeKeyListeners() {
        window.removeEventListener('keypress', this._onKeyPress);
    }

    checkAutocomplete() {

        const val = (this.form.get('name').value || '').trim().toLowerCase();

        if (val) {

            const clients = this.clients.getValue(),
                match = clients.find(c => c.name.toLowerCase() === val);

            if (match) {
                this.existingClient = match;
            } else {
                this.existingClient = null;
            }
        }

    }


}
